import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
} from '@mui/material';

interface InlineMessageComposerProps {
    open: boolean;
    recipientName: string;
    template: string;
    onClose: () => void;
    onSend: (recipientName: string, messageText: string) => void;
}

const InlineMessageComposer = ({
    open,
    recipientName,
    template,
    onClose,
    onSend,
}: InlineMessageComposerProps) => {
    const [message, setMessage] = useState(template);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) setMessage(template);
    }, [open, template]);

    const handleSend = async () => {
        setSending(true);
        try {
            await onSend(recipientName, message);
        } finally {
            setSending(false);
            setMessage('');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700, fontSize: '16px' }}>
                Message {recipientName}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    multiline
                    minRows={4}
                    maxRows={8}
                    fullWidth
                    variant="outlined"
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant="outlined" onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || !message.trim()}
                    sx={{ backgroundColor: '#6025D8', '&:hover': { backgroundColor: '#4C1D95' } }}
                >
                    {sending ? 'Sending...' : 'Send'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InlineMessageComposer;
